import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, ChevronDown, FileQuestion, Inbox, Lock, Pencil, Plus, Trash2, Unlock, Users, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Appeal,
  AppealQuestion,
  AppealQuestionInput,
  AppealRoom,
  AppealSession,
  addQuestion,
  deleteQuestion,
  deleteRoom,
  setRoomOpen,
  updateQuestion,
  VERDICTS_TO_DECIDE,
} from '../../../../services/appealsService';
import AppealQuestionForm from './AppealQuestionForm';
import {
  AppealRequest,
  ClosedNotice,
  CodeCard,
  EmptyState,
  QuestionView,
  RichTextView,
  RoomHeader,
  Tabs,
  cardCls,
  formatTime,
  primaryBtnCls,
  secondaryBtnCls,
} from './shared';

type Tab = 'questions' | 'appeals' | 'participants';

/** Final decision once three jurors have voted, otherwise how many have. */
const OutcomeBadge: React.FC<{ appeal: Appeal }> = ({ appeal }) => {
  const yes = appeal.verdicts.filter(v => v.accepted).length;
  const no = appeal.verdicts.length - yes;
  const badge =
    appeal.decision === 'accepted'
      ? { label: 'Yekun qərar: qəbul', cls: 'bg-green-600 text-white' }
      : appeal.decision === 'rejected'
        ? { label: 'Yekun qərar: rədd', cls: 'bg-red-600 text-white' }
        : {
            label: `Gözləyir · ${appeal.verdicts.length}/${VERDICTS_TO_DECIDE} səs${
              appeal.kind === 'removeQuestion' ? ' · yekdil qərar lazımdır' : ''
            }`,
            cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
          };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
      <span className={cn('rounded-full px-2 py-0.5', badge.cls)}>{badge.label}</span>
      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
        <CheckCircle2 className="w-3.5 h-3.5" /> {yes}
      </span>
      <span className="inline-flex items-center gap-1 text-red-500 dark:text-red-400">
        <XCircle className="w-3.5 h-3.5" /> {no}
      </span>
    </div>
  );
};

// ─── Questions tab ────────────────────────────────────────────────────────────

const QuestionsTab: React.FC<{ room: AppealRoom; session: AppealSession }> = ({ room, session }) => {
  const [editing, setEditing] = useState<AppealQuestion | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const nextNumber = room.questions.reduce((max, q) => Math.max(max, q.number), 0) + 1;

  const save = async (input: AppealQuestionInput) => {
    if (editing === 'new') await addQuestion(session, input);
    else if (editing) await updateQuestion(session, editing.id, input);
    toast.success('Sual saxlanıldı');
    setEditing(null);
  };

  const remove = async (questionId: number) => {
    try {
      await deleteQuestion(session, questionId);
      toast.success('Sual silindi');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sual silinmədi');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      {editing ? (
        <div className={cn(cardCls, 'p-5 sm:p-6')}>
          <h2 className="mb-4 text-left text-lg font-black text-gray-900 dark:text-gray-50">
            {editing === 'new' ? 'Yeni sual' : `Sual ${editing.number} — redaktə`}
          </h2>
          <AppealQuestionForm
            session={session}
            key={editing === 'new' ? 'new' : editing.id}
            initial={editing === 'new' ? undefined : editing}
            nextNumber={nextNumber}
            onSubmit={save}
            onCancel={() => setEditing(null)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Hər sualın öz komanda kodu var — onu həmin sual üzrə apelyasiya verən komandaya göndərin.
          </p>
          <button type="button" onClick={() => setEditing('new')} className={cn(primaryBtnCls, 'shrink-0')}>
            <Plus className="w-4 h-4" />
            Sual əlavə et
          </button>
        </div>
      )}

      {room.questions.length === 0 ? (
        <EmptyState icon={FileQuestion}>Hələ sual əlavə edilməyib. Apelyasiya veriləcək sualları buraya əlavə edin.</EmptyState>
      ) : (
        room.questions.map(q => {
          const appealCount = room.appeals.filter(a => a.questionId === q.id).length;
          return (
            <div key={q.id} className={cn(cardCls, 'p-5 sm:p-6')}>
              <div className="grid gap-5 md:grid-cols-[1fr_15rem]">
                <QuestionView question={q} />
                {q.code && (
                  <div className="md:order-last">
                    <CodeCard
                      tone="team"
                      label={`Sual ${q.number} — komanda kodu`}
                      code={q.code}
                      hint="Bu sual üçün apelyasiya verən komandaya göndərin."
                    />
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-800 pt-3">
                <span
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-semibold',
                    appealCount > 0
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  )}
                >
                  {appealCount > 0 ? `${appealCount} apelyasiya` : 'Apelyasiya yoxdur'}
                </span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setEditing(q)} className={secondaryBtnCls}>
                    <Pencil className="w-4 h-4" />
                    Redaktə
                  </button>
                  {confirmDelete === q.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => remove(q.id)}
                        className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        {appealCount > 0 ? `Sil (${appealCount} apelyasiya ilə)` : 'Bəli, sil'}
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(null)} className={secondaryBtnCls}>
                        Ləğv et
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(q.id)}
                      className={cn(secondaryBtnCls, 'text-red-600 dark:text-red-400')}
                    >
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// ─── Appeals tab ──────────────────────────────────────────────────────────────

const AppealsTab: React.FC<{ room: AppealRoom }> = ({ room }) => {
  const [expanded, setExpanded] = useState<number | null>(null);
  const questionsWithAppeals = room.questions.filter(q => room.appeals.some(a => a.questionId === q.id));

  if (questionsWithAppeals.length === 0) {
    return <EmptyState icon={Inbox}>Hələ heç bir komanda apelyasiya verməyib.</EmptyState>;
  }

  return (
    <div className="space-y-4">
      {questionsWithAppeals.map(q => {
        const appeals = room.appeals.filter(a => a.questionId === q.id);
        const open = expanded === q.id;
        return (
          <div key={q.id} className={cn(cardCls, 'p-5 sm:p-6 space-y-4')}>
            <button
              type="button"
              onClick={() => setExpanded(open ? null : q.id)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <span className="min-w-0">
                <span className="block text-base font-black text-gray-900 dark:text-gray-50">
                  Sual {q.number} · {appeals.length} apelyasiya
                </span>
                <span className="block truncate text-sm text-gray-500 dark:text-gray-400">
                  Cavab: {q.parts.map(p => p.answer).join(' / ')}
                </span>
              </span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                {open ? 'Sualı gizlət' : 'Sualı göstər'}
                <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
              </span>
            </button>
            {open && (
              <div className="rounded-xl bg-gray-50 dark:bg-gray-950 p-4">
                <QuestionView question={q} />
              </div>
            )}

            <div className="space-y-3">
              {appeals.map(a => (
                <div
                  key={a.id}
                  className={cn(
                    'rounded-xl border border-l-4 border-gray-100 dark:border-gray-800 p-4',
                    a.decision === 'accepted'
                      ? 'border-l-green-500 dark:border-l-green-500'
                      : a.decision === 'rejected'
                        ? 'border-l-red-500 dark:border-l-red-500'
                        : 'border-l-gray-300 dark:border-l-gray-600'
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-50">
                      {a.teamName}
                      <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">{formatTime(a.updatedAt)}</span>
                    </span>
                    <OutcomeBadge appeal={a} />
                  </div>
                  <div className="mt-3">
                    <AppealRequest appeal={a} />
                  </div>
                  <RichTextView html={a.text} className="mt-2" />

                  {a.verdicts.length > 0 && (
                    <ul className="mt-4 space-y-2 rounded-xl bg-gray-50 dark:bg-gray-950 p-3">
                      {a.verdicts.map(v => (
                        <li key={v.id} className="flex gap-2 text-sm">
                          {v.accepted ? (
                            <CheckCircle2 className="mt-0.5 w-4 h-4 shrink-0 text-green-500" />
                          ) : (
                            <XCircle className="mt-0.5 w-4 h-4 shrink-0 text-red-400" />
                          )}
                          <div className="min-w-0">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{v.juryName}</span>
                            <span className="text-gray-400 dark:text-gray-500"> · {formatTime(v.updatedAt)}</span>
                            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{v.text}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Participants tab ─────────────────────────────────────────────────────────

const ParticipantsTab: React.FC<{ room: AppealRoom }> = ({ room }) => {
  const teams = room.participants.filter(p => p.role === 'team');
  const juries = room.participants.filter(p => p.role === 'jury');

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className={cn(cardCls, 'p-5')}>
        <h3 className="mb-3 text-left text-base font-black text-gray-900 dark:text-gray-50">Komandalar ({teams.length})</h3>
        {teams.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Hələ komanda qoşulmayıb.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {teams.map(t => {
              const question = room.questions.find(q => q.id === t.questionId);
              const filed = room.appeals.some(a => a.questionId === t.questionId && a.teamName === t.name);
              return (
                <li key={`${t.questionId}-${t.name}`} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span className="truncate font-medium text-gray-900 dark:text-gray-100">{t.name}</span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {question ? `Sual ${question.number}` : ''} · {filed ? 'apelyasiya verib' : 'hələ yazır'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className={cn(cardCls, 'p-5')}>
        <h3 className="mb-3 text-left text-base font-black text-gray-900 dark:text-gray-50">Jürilər ({juries.length})</h3>
        {juries.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Hələ jüri qoşulmayıb.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {juries.map(j => {
              const done = room.appeals.filter(a => a.verdicts.some(v => v.juryName === j.name)).length;
              return (
                <li key={j.name} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span className="truncate font-medium text-gray-900 dark:text-gray-100">{j.name}</span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {done} / {room.appeals.length} rəy
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Owner view ───────────────────────────────────────────────────────────────

const AppealOwnerView: React.FC<{ room: AppealRoom; session: AppealSession; onLeave: () => void }> = ({
  room,
  session,
  onLeave,
}) => {
  const [tab, setTab] = useState<Tab>('questions');
  const [toggling, setToggling] = useState(false);

  const removeRoom = async () => {
    if (!window.confirm('Otaq, bütün suallar, apelyasiyalar və rəylər silinəcək. Davam edilsin?')) return;
    try {
      await deleteRoom(session);
      toast.success('Otaq silindi');
      onLeave();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Otaq silinmədi');
    }
  };

  const pendingCount = useMemo(() => room.appeals.filter(a => !a.decided).length, [room.appeals]);

  const toggleOpen = async () => {
    setToggling(true);
    try {
      await setRoomOpen(session, !room.isOpen);
      toast.success(room.isOpen ? 'Otaq bağlandı' : 'Otaq açıldı');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Xəta baş verdi');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="space-y-4">
      <RoomHeader
        room={room}
        role="owner"
        name=""
        onLeave={onLeave}
        actions={
          <button type="button" onClick={toggleOpen} disabled={toggling} className={secondaryBtnCls}>
            {room.isOpen ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            {room.isOpen ? 'Otağı bağla' : 'Otağı aç'}
          </button>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <CodeCard tone="jury" label="Jüri kodu" code={room.juryCode ?? ''} hint="Yalnız jürilərlə paylaşın." />
        <CodeCard tone="owner" label="Sizin kodunuz" code={room.ownerCode ?? ''} hint="Otağa qayıtmaq üçün saxlayın. Heç kimlə paylaşmayın." />
      </div>

      {!room.isOpen && <ClosedNotice>Otaq bağlıdır: komandalar yeni apelyasiya verə, jürilər rəy dəyişə bilməz.</ClosedNotice>}

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'questions', label: 'Suallar', count: room.questions.length },
          {
            key: 'appeals',
            label: 'Apelyasiyalar',
            count: room.appeals.length,
            dot: pendingCount > 0 ? `${pendingCount} qərarsız` : undefined,
          },
          { key: 'participants', label: 'İştirakçılar', count: room.participants.length, icon: Users },
        ]}
      />

      {tab === 'questions' && <QuestionsTab room={room} session={session} />}
      {tab === 'appeals' && <AppealsTab room={room} />}
      {tab === 'participants' && <ParticipantsTab room={room} />}

      <div className="flex justify-end border-t border-gray-200 dark:border-gray-800 pt-4">
        <button type="button" onClick={removeRoom} className={cn(secondaryBtnCls, 'text-red-600 dark:text-red-400')}>
          <Trash2 className="w-4 h-4" />
          Otağı sil
        </button>
      </div>
    </div>
  );
};

export default AppealOwnerView;
